import { CreateCaseFormPayload } from "../utils/helper";
import StepNavigation from "./StepNavigation";
import {
    LoanAccount,
    PrimaryParty,
    relatedAsLabel,
} from "../../../api/creditAccounts";
import {
    Account,
    ACCOUNT_TYPE_LABELS,
    ACCOUNT_CATEGORY_LABELS,
    AccountType as DepositAccountType,
    AccountCategory,
    isEmployeeRole,
} from "../../../api/accounts";
import { EnrichedStakeholder } from "../../../api/stakeholders";
import { AreaOfFraud } from "../../../api/cases";
import { deriveEmployees } from "./party-involved/derive";
import { useMemo } from "react";
import { CREDIT_FACILITY_LABELS, CreditFacility } from "../../../api/credit_accounts";
import { AddAccountPartyRow } from "./party-involved/AccountPartyRow";

type CaseReviewStepProps = {
    payload: CreateCaseFormPayload;
    onSubmit: () => void;
    onPrevious: () => void;
    isBusy: boolean;
    showLegacyPartyInvolved: boolean;
    loanAccounts: LoanAccount[];
    parties: PrimaryParty[];
    accounts?: Account[];
    accountParties?: AddAccountPartyRow[];
    showAccountPartyInvolved?: boolean;
};

interface ReviewTemplatePros {
    title: string;
    children: React.ReactNode;
};

type ReviewBaseDetailsProps = Pick<CreateCaseFormPayload, 'source' | 'referrer_name' | 'referral_date' | 'region' | 'zone' | 'branch' | 'forwarding_authority'>;
type ReviewBackgroundDetailsProps = Pick<CreateCaseFormPayload, 'investigation_nature' | 'background'>;

const CaseReviewTemplate = (props: ReviewTemplatePros) => {
    return (
        <div className="border flex flex-col rounded-xl border-[#E5E5E5]">
            <div className="bg-slate-100 px-4 py-3 rounded-inherit rounded-bl-none rounded-br-none">
                <h3 className="text-lg font-medium">{props.title}</h3>
            </div>
            <div>{props.children}</div>
        </div>
    );
};

const CaseReviewBasicDetails = (props: ReviewBaseDetailsProps) => {
    return (
        <CaseReviewTemplate title="Basic Details">
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 gap-y-5 p-4">
                <div className="flex flex-col gap-1">
                    <div className="font-medium text-sm">Source</div>
                    <div className="text-sm text-slate-600">{props.source?.name}</div>
                </div>
                <div className="flex flex-col gap-1">
                    <div className="font-medium text-sm">Referrer Name</div>
                    <div className="text-sm text-slate-600">{props.referrer_name}</div>
                </div>
                <div className="flex flex-col gap-1">
                    <div className="font-medium text-sm">Referral Date</div>
                    <div className="text-sm text-slate-600">{props.referral_date}</div>
                </div>
                <div className="flex flex-col gap-1">
                    <div className="font-medium text-sm">Region</div>
                    <div className="text-sm text-slate-600">{props.region?.name}</div>
                </div>
                <div className="flex flex-col gap-1">
                    <div className="font-medium text-sm">Zone</div>
                    <div className="text-sm text-slate-600">{props.zone?.name}</div>
                </div>
                <div className="flex flex-col gap-1">
                    <div className="font-medium text-sm">Branch</div>
                    <div className="text-sm text-slate-600">{props.branch?.name}</div>
                </div>
                <div className="flex flex-col gap-1">
                    <div className="font-medium text-sm">Forwarding Authority</div>
                    <div className="text-sm text-slate-600">
                        {props.forwarding_authority?.name ?? "-"}
                    </div>
                </div>
            </div>
        </CaseReviewTemplate>
    );
};

const CaseReviewBackgroundDetails = (props: ReviewBackgroundDetailsProps) => {
    return (
        <CaseReviewTemplate title="Background">
            <div className="grid md:grid-cols-1 gap-4 gap-y-5 p-4">
                <div className="flex flex-col gap-1">
                    <div className="font-medium text-sm">Nature of Suspicion</div>
                    <div className="text-sm text-slate-600">{props.investigation_nature?.map((item) => item.name).join(", ")}</div>
                </div>
                <div className="flex flex-col gap-1 min-w-0">
                    <div className="font-medium text-sm">Background</div>
                    <div className="text-sm text-slate-600 whitespace-pre-wrap break-words">
                        {props.background}
                    </div>
                </div>
            </div>
        </CaseReviewTemplate>
    );
};

interface ReviewPartyInvolvedProps {
    payload: CreateCaseFormPayload;
    showLegacyPartyInvolved: boolean;
    loanAccounts: LoanAccount[];
    parties: PrimaryParty[];
    accounts?: Account[];
    accountParties?: AddAccountPartyRow[];
    showAccountPartyInvolved?: boolean;
}

const CaseReviewPartyInvolvedDetails = ({
    payload,
    showLegacyPartyInvolved,
    loanAccounts,
    parties,
    accounts = [],
    accountParties = [],
    showAccountPartyInvolved = false,
}: ReviewPartyInvolvedProps) => {
    const employees = useMemo(() => deriveEmployees(parties), [parties]);
    const areaOfFraud = payload.area_of_fraud?.value as AreaOfFraud | undefined;
    const accountEmployees = useMemo(() => {
        if (!areaOfFraud) return [];
        return accountParties
            .filter((p) =>
                (p.relationshipGroups || []).some(
                    (r) => r.relatedAs && isEmployeeRole(r.relatedAs, areaOfFraud),
                ),
            )
            .map((p) => {
                const types = (p.relationshipGroups || [])
                    .filter((r) => r.relatedAs && isEmployeeRole(r.relatedAs, areaOfFraud))
                    .map((r) => r.relatedAs!);
                return {
                    key: p.stakeholder.stakeholder_id,
                    name: p.stakeholder.name,
                    types,
                    employeeId: p.employee_details?.employee_id,
                };
            });
    }, [accountParties, areaOfFraud]);

    const extAccountNumberById = Object.fromEntries(
        loanAccounts.map((account) => [
            account.accountNumber,
            account.extAccountNumber,
        ]),
    );
    const depositExtAccountNumberById = Object.fromEntries(
        accounts.map((account) => [
            account.account_number,
            account.ext_account_number,
        ]),
    );

    return (
        <CaseReviewTemplate title="Party Involved">
            <div className="flex flex-col gap-5 p-4">
                <div className="flex flex-col gap-1">
                    <div className="font-medium text-sm">Area of Fraud</div>
                    <div className="text-sm text-slate-600">{payload.area_of_fraud?.name ?? "-"}</div>
                </div>

                {showAccountPartyInvolved ? (
                    <>
                        <div className="flex flex-col gap-2">
                            <div className="font-medium text-sm">Accounts</div>
                            <div className="text-sm text-slate-600 flex flex-col gap-1">
                                {accounts.length > 0 ? accounts.map((a) => (
                                    <div key={a.account_number}>
                                        <span className="font-medium">{a.ext_account_number || "-"}</span>
                                        {" • "}{a.account_type ? ACCOUNT_TYPE_LABELS[a.account_type as DepositAccountType] : "-"}
                                        {" • "}{a.account_category ? ACCOUNT_CATEGORY_LABELS[a.account_category as AccountCategory] : "-"}
                                        {" • "}{a.account_name || "-"}
                                    </div>
                                )) : "-"}
                            </div>
                        </div>

                        <div className="flex flex-col gap-2">
                            <div className="font-medium text-sm">Primary Parties</div>
                            <div className="text-sm text-slate-600 flex flex-col gap-1">
                                {accountParties.length > 0 ? accountParties.map((p) => {
                                    const byRole = new Map<string, string[]>();
                                    (p.relationshipGroups || []).forEach((r) => {
                                        if (!r.relatedAs) return;
                                        const label = relatedAsLabel(r.relatedAs);
                                        const ext = r.accountNumbers.map(a => depositExtAccountNumberById[a] || a);
                                        byRole.set(label, ext);
                                    });
                                    return (
                                        <div key={p.stakeholder.stakeholder_id}>
                                            <span className="font-medium">{p.stakeholder.name || "-"}</span>
                                            {": "}
                                            {Array.from(byRole.entries())
                                                .map(([type, accts]) => `${type}${accts.length ? ` (${accts.join(", ")})` : ""}`)
                                                .join("; ") || "-"}
                                        </div>
                                    );
                                }) : "-"}
                            </div>
                        </div>

                        <div className="flex flex-col gap-2">
                            <div className="font-medium text-sm">Employees</div>
                            <div className="text-sm text-slate-600 flex flex-col gap-1">
                                {accountEmployees.length > 0 ? accountEmployees.map((e) => (
                                    <div key={e.key}>
                                        <span className="font-medium">{e.name || "-"}</span>
                                        {" • "}{e.types.map(relatedAsLabel).join(", ")}
                                        {e.employeeId ? ` • ${e.employeeId}` : ""}
                                    </div>
                                )) : "-"}
                            </div>
                        </div>
                    </>
                ) : showLegacyPartyInvolved ? (
                    <>
                        <div className="flex flex-col gap-1">
                            <div className="font-medium text-sm">Account Type</div>
                            <div className="text-sm text-slate-600">{payload.account_type?.name ?? "-"}</div>
                        </div>

                        <div className="flex flex-col gap-2">
                            <div className="font-medium text-sm">Account Numbers</div>
                            <div className="text-sm text-slate-600 flex flex-col gap-1">
                                {payload.loanDetails && payload.loanDetails.length > 0 ? payload.loanDetails.map((row) => (
                                    <div key={row.id}>
                                        <span className="font-medium">{row.product}:</span>{" "}
                                        {row.loanReferenceIds.join(", ")}
                                    </div>
                                )) : "-"}
                            </div>
                        </div>

                        <div className="flex flex-col gap-1">
                            <div className="font-medium text-sm">Employee Involvement</div>
                            <div className="text-sm text-slate-600">{payload.has_employee_involvement ? "Yes" : "No"}</div>
                        </div>
                        <div className="flex flex-col gap-1">
                            <div className="font-medium text-sm">Employee IDs</div>
                            <div className="text-sm text-slate-600">
                                {payload.employee_ids && payload.employee_ids.length > 0 ? payload.employee_ids.join(", ") : "-"}
                            </div>
                        </div>

                        <div className="flex flex-col gap-1">
                            <div className="font-medium text-sm">Parties Involved</div>
                            <div className="text-sm text-slate-600 flex flex-col gap-1">
                                {payload.parties_involved && payload.parties_involved.length > 0 ? payload.parties_involved.map((party, index) => (
                                    <span key={index}>{party.name} ({party.party_type?.replace(/_/g, " ")})</span>
                                )) : "-"}
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="flex flex-col gap-2">
                            <div className="font-medium text-sm">Loan Accounts</div>
                            <div className="text-sm text-slate-600 flex flex-col gap-1">
                                {loanAccounts.length > 0 ? loanAccounts.map((a) => (
                                    <div key={a.accountNumber}>
                                        <span className="font-medium">{a.extAccountNumber || "-"}</span>
                                        {" • "}{a.product || "-"}
                                        {" • "}{CREDIT_FACILITY_LABELS[a.creditFacility as CreditFacility] ?? a.creditFacility}
                                        {" • "}{a.accountName || "-"}
                                    </div>
                                )) : "-"}
                            </div>
                        </div>

                        <div className="flex flex-col gap-2">
                            <div className="font-medium text-sm">Primary Parties</div>
                            <div className="text-sm text-slate-600 flex flex-col gap-1">
                                {parties.length > 0 ? parties.map((p) => (
                                    <div key={p.id}>
                                        <span className="font-medium">{p.name || "-"}</span>
                                        {": "}
                                        {p.account_relationships.map((r) => {
                                            const type = relatedAsLabel(r.related_as);
                                            const accts = r.account_numbers
                                                .map((accountNumber) => extAccountNumberById[accountNumber])
                                                .filter(Boolean)
                                                .join(", ");
                                            return `${type}${accts ? ` (${accts})` : ""}`;
                                        }).join("; ")}
                                    </div>
                                )) : "-"}
                            </div>
                        </div>

                        <div className="flex flex-col gap-2">
                            <div className="font-medium text-sm">Employees</div>
                            <div className="text-sm text-slate-600 flex flex-col gap-1">
                                {employees.length > 0 ? employees.map((e) => (
                                    <div key={e.key}>
                                        <span className="font-medium">{e.name || "-"}</span>
                                        {" • "}{e.types.map(relatedAsLabel).join(", ")}
                                        {e.employeeId ? ` • ${e.employeeId}` : ""}
                                    </div>
                                )) : "-"}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </CaseReviewTemplate>
    );
};

const CaseReviewStep: React.FC<CaseReviewStepProps> = ({
    payload,
    onSubmit,
    onPrevious,
    isBusy,
    showLegacyPartyInvolved,
    loanAccounts,
    parties,
    accounts,
    accountParties,
    showAccountPartyInvolved,
}) => {
    return (
        <>
            <div className="flex flex-col gap-5">
                <h2 className="text-lg font-medium">Review & Submit</h2>
                <hr className="h-px bg-[#E5E5E5] border-0"/>
                <CaseReviewBasicDetails {...payload}/>
                <CaseReviewBackgroundDetails {...payload}/>
                <CaseReviewPartyInvolvedDetails
                    payload={payload}
                    showLegacyPartyInvolved={showLegacyPartyInvolved}
                    loanAccounts={loanAccounts}
                    parties={parties}
                    accounts={accounts}
                    accountParties={accountParties}
                    showAccountPartyInvolved={showAccountPartyInvolved}
                />
            </div>
            <StepNavigation onNext={onSubmit} onPrevious={onPrevious} isLast disabled={isBusy} />
        </>
    );
};

export default CaseReviewStep;
